/**
 * ServiceEditor — Full-page add / edit editor for services
 * Mirip pola BlogEditor: VIEW enum, upload gambar, no window.reload()
 */
import React, { useState, useRef } from "react";
import Swal from "sweetalert2";
import { ImgWithFallback } from "../../invalidImage";
import { insertService, updateService } from "../../../api/servicesAPI";

const BASE = process.env.REACT_APP_API_BASE_URL || "http://localhost:4000";

// ── Upload helper ─────────────────────────────────────────────
async function uploadImageFile(file) {
  const token = localStorage.getItem("accessToken") || "";
  const form = new FormData();
  form.append("image", file);
  const res = await fetch(`${BASE}/api/upload?type=service`, {
    method: "POST",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: form,
  });
  if (!res.ok) throw new Error("Upload gagal");
  const data = await res.json();
  return data.url;
}

// ── Image Picker (URL + Upload) ───────────────────────────────
function ImagePicker({ imageUrl, onChange }) {
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadImageFile(file);
      onChange(url);
    } catch (err) {
      Swal.fire({ icon: "error", title: "Upload gagal", text: err.message });
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        <input
          type="text"
          value={imageUrl}
          onChange={(e) => onChange(e.target.value)}
          placeholder="https://... atau upload file →"
          className="flex-1 px-3 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:border-brand-400 focus:ring-2 focus:ring-brand-100 outline-none font-overpass text-sm text-slate-800 placeholder:text-slate-400 transition-all"
        />
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="shrink-0 px-4 py-2.5 rounded-xl bg-gradient-to-r from-brand-600 to-eco-600 text-white text-sm font-semibold shadow-lg shadow-brand-500/20 hover:-translate-y-0.5 transition-all disabled:opacity-60"
        >
          {uploading ? "⏳" : "⬆️ Upload"}
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleFileChange}
        />
      </div>
      <div className="relative rounded-xl overflow-hidden border border-slate-200 bg-slate-50 aspect-[16/7]">
        <ImgWithFallback
          src={imageUrl}
          alt="Preview"
          className="w-full h-full object-cover"
        />
        {!imageUrl && (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-slate-400">
            <div className="text-3xl mb-1">⚙️</div>
            <p className="text-xs font-overpass">Preview gambar service</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────
const ServiceEditor = ({ service = null, onSuccess, onCancel }) => {
  const isEdit = Boolean(service);

  const [name, setName]               = useState(service?.name || "");
  const [imageUrl, setImageUrl]       = useState(service?.image || "");
  const [description, setDescription] = useState(service?.description || "");
  const [saving, setSaving]           = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !description.trim()) {
      Swal.fire({ icon: "error", title: "Form tidak lengkap", text: "Nama dan deskripsi wajib diisi." });
      return;
    }

    const confirm = isEdit
      ? await Swal.fire({
          icon: "question",
          title: "Update service?",
          text: "Perubahan akan langsung tersimpan.",
          showCancelButton: true,
          confirmButtonText: "Ya, update!",
          cancelButtonText: "Batal",
        })
      : { isConfirmed: true };

    if (!confirm.isConfirmed) return;

    setSaving(true);
    try {
      Swal.fire({
        title: isEdit ? "Menyimpan..." : "Menambahkan...",
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading(),
      });

      const payload = {
        name: name.trim(),
        image: imageUrl,
        description: description.trim(),
      };

      if (isEdit) {
        await updateService(service.id, payload);
      } else {
        await insertService(payload);
      }

      await Swal.fire({
        icon: "success",
        title: "Berhasil!",
        text: isEdit ? "Service berhasil diupdate." : "Service berhasil ditambahkan.",
      });
      if (onSuccess) onSuccess();
    } catch (err) {
      Swal.fire({ icon: "error", title: "Gagal", text: err.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Top Bar */}
      <div className="sticky top-0 z-50 bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-screen-xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={onCancel}
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-slate-800 transition-colors"
            >
              ← Kembali
            </button>
            <span className="text-slate-300">/</span>
            <span className="text-sm font-semibold text-slate-700 font-overpass">
              {isEdit ? `Edit: ${(service.name || "").slice(0, 40)}` : "Service Baru"}
            </span>
          </div>

          <button
            type="submit"
            form="service-editor-form"
            disabled={saving}
            className="inline-flex items-center gap-2 px-5 py-2 rounded-xl bg-gradient-to-r from-brand-600 to-eco-600 text-white font-semibold text-sm shadow-lg shadow-brand-500/30 hover:shadow-brand-500/50 transition-all hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-y-0"
          >
            {saving ? (
              <>
                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Menyimpan...
              </>
            ) : (
              <>{isEdit ? "💾 Update Service" : "✨ Simpan Service"}</>
            )}
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="max-w-screen-xl mx-auto px-4 sm:px-6 py-8">
        <div className="grid lg:grid-cols-2 gap-6 items-start">
          {/* ── Form ── */}
          <form id="service-editor-form" onSubmit={handleSubmit} className="space-y-5">
            {/* Name */}
            <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-soft">
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 font-overpass">
                Nama Layanan <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={name}
                maxLength={150}
                onChange={(e) => setName(e.target.value)}
                placeholder="Nama service / layanan..."
                className="w-full px-3 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:border-brand-400 focus:ring-2 focus:ring-brand-100 outline-none font-overpass text-sm text-slate-800 placeholder:text-slate-400 transition-all"
              />
              <p className="text-xs text-slate-400 font-overpass mt-1.5 text-right">
                {name.length}/150
              </p>
            </div>

            {/* Image */}
            <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-soft">
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3 font-overpass">
                Gambar Service
              </label>
              <ImagePicker imageUrl={imageUrl} onChange={setImageUrl} />
            </div>

            {/* Description */}
            <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-soft">
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 font-overpass">
                Deskripsi <span className="text-red-500">*</span>
              </label>
              <textarea
                value={description}
                maxLength={1000}
                rows={6}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Jelaskan tentang layanan ini..."
                className="w-full px-3 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:border-brand-400 focus:ring-2 focus:ring-brand-100 outline-none font-overpass text-sm text-slate-800 placeholder:text-slate-400 transition-all resize-none"
              />
              <p className="text-xs text-slate-400 font-overpass mt-1.5 text-right">
                {description.length}/1000
              </p>
            </div>

            {/* Mobile submit */}
            <div className="sm:hidden pt-2">
              <button
                type="submit"
                disabled={saving}
                className="w-full py-3 rounded-xl bg-gradient-to-r from-brand-600 to-eco-600 text-white font-semibold shadow-lg shadow-brand-500/30 transition-all hover:-translate-y-0.5 disabled:opacity-60"
              >
                {isEdit ? "💾 Update Service" : "✨ Simpan Service"}
              </button>
            </div>
          </form>

          {/* ── Preview ── */}
          <div className="hidden lg:block">
            <div className="sticky top-20">
              <div className="bg-white rounded-2xl border border-slate-200 shadow-soft overflow-hidden">
                <div className="flex items-center gap-2 px-5 py-3 border-b border-slate-100 bg-slate-50">
                  <span className="w-2 h-2 rounded-full bg-green-400" />
                  <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider font-overpass">
                    Preview
                  </span>
                </div>
                <div className="p-6 space-y-4">
                  {/* Image */}
                  <div className="rounded-xl overflow-hidden aspect-[16/7] bg-slate-100 border border-slate-100">
                    <ImgWithFallback
                      src={imageUrl}
                      alt="preview"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  {/* Name */}
                  <h2 className="font-oswald text-xl font-semibold text-slate-900 leading-snug">
                    {name || <span className="text-slate-300">Nama layanan...</span>}
                  </h2>
                  {/* Description */}
                  <p className="text-sm text-slate-600 font-overpass leading-relaxed">
                    {description || <span className="text-slate-300 italic">Deskripsi layanan...</span>}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServiceEditor;
